import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, HeartFill } from "react-bootstrap-icons";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/authContext";
import { checkAuth } from "../../utils/auth";
import { useSearchParams } from "react-router-dom";
const API = process.env.REACT_APP_API_URL;

// Carousel settings (used only for Shop by Category)
const CARD_BASE_WIDTH = 230;
const GAP = 16;

const getVisibleCount = (screenWidth) => {
  if (screenWidth < 640) return 2;
  if (screenWidth < 768) return 3;
  if (screenWidth < 1100) return 4;
  return 5;
};

const getCardWidth = (screenWidth) => {
  if (screenWidth < 640) {
    // compute two cards with some padding; never drop below base width
    const calculated = Math.floor((screenWidth - 40 - GAP) / 2);
    return Math.max(CARD_BASE_WIDTH, calculated);
  }
  return CARD_BASE_WIDTH;
};

const ProductsPage = () => {
  const {
    fetchWishlist,
    wishlist,
    handleAddToWishlist,
    fetchProducts,
    products,
    handleRemoveFromWishlist,
  } = useAuth();

  const navigate = useNavigate();

  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : CARD_BASE_WIDTH * 5
  );
  const VISIBLE_COUNT = getVisibleCount(screenWidth);
  const CARD_WIDTH = getCardWidth(screenWidth);
  const [searchParams] = useSearchParams();
const selectedCategory = searchParams.get("category");
  useEffect(() => {
    fetchProducts();
    fetchWishlist();

    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line
  }, []);

 useEffect(() => {
  if (selectedCategory) {
    // Small delay to let the page render first
    setTimeout(() => {
      const section = document.getElementById(`category-${selectedCategory}`);
      if (section) {
        section.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
        
        // Optional: Highlight the section temporarily
        section.style.transition = "all 0.6s";
        section.style.backgroundColor = "#fee2e2";
        setTimeout(() => {
          section.style.backgroundColor = "transparent";
        }, 1800);
      }
    }, 300);
  }
}, [selectedCategory]);
  const productsByCategory = useMemo(() => {
    const inStock = products.filter((p) => p.inStock > 0);
    return inStock.reduce((acc, product) => {
      const category = product.category || "Other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(product);
      return acc;
    }, {});
  }, [products]);

  // Slider state — only for categories
  const [categoryIndex, setCategoryIndex] = useState({});

  useEffect(() => {
    const indexes = {};
    Object.keys(productsByCategory).forEach((cat) => {
      indexes[cat] = 0;
    });
    setCategoryIndex(indexes);
  }, [productsByCategory]);

  const handleCategoryNext = (category) => {
    setCategoryIndex((prev) => {
      const total = productsByCategory[category]?.length || 0;
      const max = Math.max(0, total - VISIBLE_COUNT);
      return { ...prev, [category]: Math.min((prev[category] ?? 0) + 1, max) };
    });
  };

  const handleCategoryPrev = (category) => {
    setCategoryIndex((prev) => ({
      ...prev,
      [category]: Math.max((prev[category] ?? 0) - 1, 0),
    }));
  };
  const handleNext = (category) => {
    setCategoryIndex((prev) => {
      const total = productsByCategory[category].length;
      const maxIndex = Math.max(0, total - VISIBLE_COUNT);

      return {
        ...prev,
        [category]: Math.min((prev[category] ?? 0) + 1, maxIndex),
      };
    });
  };

  const handlePrev = (category) => {
    setCategoryIndex((prev) => ({
      ...prev,
      [category]: Math.max((prev[category] ?? 0) - 1, 0),
    }));
  };
  // Recent products — same card style, grid layout
  const recentProducts = products
    .filter((p) => p.inStock > 0)
    .slice(-5)
    .reverse();

  const outOfStockProducts = products.filter((p) => p.inStock === 0);

  const handleCardClick = (id) => navigate(`/product/medical/${id}`);
const handleWishlistToggle = async (product_id) => {
  if (!checkAuth()) {
    toast.error("Session expired. Please login to continue");
    return navigate(`/auth?tab=register`);
  }

  const isInWishlist = wishlist.some(item => item.product_id === product_id);
  const product = products.find(p => p.id === product_id);

  if (!product) {
    toast.error("Product not found");
    return;
  }

  try {
    if (isInWishlist) {
      await handleRemoveFromWishlist(product_id);
      toast.success("Removed from wishlist");
    } else {
      await handleAddToWishlist(product); // ✅ send full product
      toast.success("Added to wishlist!");
    }
  } catch (err) {
    console.error(err);
    toast.error("Failed to update wishlist");
  }
};

  if (!products.length) {
    return (
      <div className="py-12 text-center text-gray-600">
        <h3 className="text-xl font-medium">No products found</h3>
      </div>
    );
  }

  // ── Unified Product Card (used in both sections) ────────────────────────────────
  const ProductCard = ({ product, showNewBadge = false }) => {
    const isInWishlist = wishlist.some((item) => item.product_id === product.product_id);

    return (
      <div 
        // 1. REMOVED max-w-[230px] from className
        className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 w-full"
        // 2. ADDED as inline style
        style={{ maxWidth: '230px' }} 
      >
        {/* Image Container */}
        <div
          onClick={() => handleCardClick(product.product_id)}
          // 3. REMOVED aspect-[4/5] from className
          className="cursor-pointer bg-gray-50 relative overflow-hidden"
          // 4. ADDED as inline style
          style={{ aspectRatio: '4/5' }} 
        >
          <img
            src={`${API}${product.image_url}`}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 lazy"
            loading="lazy"
          />

          {showNewBadge && product.inStock > 0 && (
            <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
              New
            </span>
          )}

          {product.inStock === 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-gray-900/90 text-white px-5 py-2 rounded-lg text-sm font-medium backdrop-blur-sm">
                Out of Stock
              </span>
            </div>
          )}

          {/* Wishlist */}
           <div
              className="absolute top-2 right-2 text-lg sm:text-xl cursor-pointer transition-transform hover:scale-125 z-10"
              onClick={(e) => {
                e.stopPropagation();
                handleWishlistToggle(product.product_id, product.category);
              }}
              style={{ color: isInWishlist ? "red" : "#ccc" }}
            >
              {isInWishlist ? <HeartFill /> : <Heart />}
           </div>
        </div>

        {/* Info */}
        <div className="p-4 text-center space-y-1.5">
          <h5 className="font-medium text-base line-clamp-2 min-h-[2.8rem] text-gray-900">
            {product.name}
          </h5>
          <p className="text-sm text-gray-600 line-clamp-1">
            {product.smalldescription || "—"}
          </p>
          <p className="text-red-600 font-bold text-lg pt-1">
            ₹{product.price}
          </p>
        </div>
      </div>
    );
  };

  // Professional Arrow Button (only for Shop by Category)
  

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="w-full pb-12">

      {/* Recent Products – grid, more breathing room at top */}
      {recentProducts.length > 0 && (
        <div className="container mx-auto px-4 sm:px-6 md:px-8 pt-8 sm:pt-10 mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-10">
            Recent Products
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 sm:gap-6">
            {recentProducts.map((product) => (
              <ProductCard
                key={product.product_id}
                product={product}
                showNewBadge
              />
            ))}
          </div>
        </div>
      )}

      {/* Shop by Category – carousel with professional arrows */}
      <div className="bg-gray-50 py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10 sm:mb-12">
            Shop by Category
          </h2>

          <div className="space-y-12 sm:space-y-16">
            {Object.keys(productsByCategory).map((category) => {
              const items = productsByCategory[category];
              if (!items?.length) return null;

              const currentIndex = categoryIndex[category] || 0;
              const canGoNext = currentIndex + VISIBLE_COUNT < items.length;

              return (
                <div key={category} className="relative" id={`category-${category.toLowerCase().replace(/\s+/g, "-")}`}>
                  <h3 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8 capitalize">
                    {category}
                  </h3>

                  <div className="relative px-4 md:px-12 lg:px-16">
                    {/* Left Arrow */}
                    <button
                    onClick={() => handlePrev(category)}
                    disabled={categoryIndex[category] === 0}
                    className={`
                      absolute left-0 top-1/2 -translate-y-1/2 z-20
                      p-1.5 sm:p-2 md:p-3 rounded-full transition-all
                      ${categoryIndex[category] === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-red-500 text-white hover:bg-red-600 hover:shadow-lg cursor-pointer active:scale-95'
                      }
                    `}
                    aria-label="Previous products"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                    {/* Carousel */}
                    <div
                      className="overflow-hidden mx-auto"
                      style={{
                        maxWidth: `${Math.max(0, VISIBLE_COUNT * (CARD_WIDTH + GAP) - GAP)}px`,
                      }}
                    >
                      <div
                        className="flex transition-transform duration-500 ease-out"
                        style={{
                          gap: `${GAP}px`,
                          transform: `translateX(-${currentIndex * (CARD_WIDTH + GAP)}px)`,
                        }}
                      >
                        {items.map((product) => (
                          <div
                            key={product.product_id}
                            style={{ width: CARD_WIDTH, flexShrink: 0 }}
                          >
                            <ProductCard product={product} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                    onClick={() => handleNext(category)}
                    disabled={categoryIndex[category] + VISIBLE_COUNT >= productsByCategory[category].length}
                    className={`
                      absolute right-0 top-1/2 -translate-y-1/2 z-20
                      p-1.5 sm:p-2 md:p-3 rounded-full transition-all
                      ${categoryIndex[category] + VISIBLE_COUNT >= productsByCategory[category].length
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-red-500 text-white hover:bg-red-600 hover:shadow-lg cursor-pointer active:scale-95'
                      }
                    `}
                    aria-label="Next products"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Out of Stock */}
      {outOfStockProducts.length > 0 && (
        <div className="container mx-auto px-4 sm:px-6 md:px-8 mt-12 sm:mt-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-10">
            Out of Stock
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 sm:gap-6">
            {outOfStockProducts.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;